import window from 'global/window';
import logger from '../common/logger';

const CFP_METHODS = new Set([
  'isLoggedIn',
  'getIdToken',
  'getAccessToken',
  'getUserProfile',
  'login',
  'logout',
  'signIntoAnother'
]);

// Initialize host-side bridge for CFP auth requests coming from an iframe.
// eslint-disable-next-line import/prefer-default-export
export const initCfpAuthBridge = ({ allowedOrigins = null } = {}) => {
  const isOriginAllowed = (origin) => {
    if (!allowedOrigins || !allowedOrigins.length) {
      // No allowlist configured: allow but log once.
      return true;
    }
    return allowedOrigins.includes(origin);
  };

  const handleCfpAuthRequest = async (event) => {
    const { data, origin, source } = event || {};
    if (!data || data.type !== 'CFP_AUTH_PROVIDER') return;

    if (!isOriginAllowed(origin)) {
      logger.warn('[CFP] Ignoring message from untrusted origin', origin);
      return;
    }

    const cfpAuth = window?.cfp?.providers?.auth;
    if (!cfpAuth) {
      logger.warn('[CFP] Auth provider not available');
      return;
    }

    const method = data.CFPAuthMethod;
    if (!CFP_METHODS.has(method)) {
      const response = {
        message: `Error - no handler for CFPAuthMethod ${method}`
      };
      source?.postMessage({ type: 'CFP_RESPONSE', response }, origin || '*');
      return;
    }

    try {
      let response;
      switch (method) {
        case 'isLoggedIn':
          response = await cfpAuth.isLoggedIn();
          break;
        case 'getIdToken': {
          const isLoggedIn = await cfpAuth.isLoggedIn();
          if (!isLoggedIn) throw new Error('User is not logged in');
          response = await cfpAuth.getIdToken();
          break;
        }
        case 'getAccessToken': {
          const isLoggedIn = await cfpAuth.isLoggedIn();
          if (!isLoggedIn) throw new Error('User is not logged in');
          response = await cfpAuth.getAccessToken();
          break;
        }
        case 'getUserProfile':
          response = await cfpAuth.getUserProfile();
          break;
        case 'login':
          response = await cfpAuth.login();
          break;
        case 'logout':
          response = await cfpAuth.logout({
            logoutRedirectUri: window.location.href
          });
          break;
        case 'signIntoAnother': {
          const isLoggedIn = await cfpAuth.isLoggedIn();
          if (isLoggedIn) {
            try {
              sessionStorage.setItem('aa-trigger-signin', 'true');
            } catch (error) {
              logger.warn('[CFP] Failed to set aa-trigger-signin', error);
            }
            response = await cfpAuth.logout({
              logoutRedirectUri: window.location.href
            });
          } else {
            response = await cfpAuth.login();
          }
          break;
        }
        default:
          response = undefined;
      }

      source?.postMessage({ type: 'CFP_RESPONSE', response }, origin || '*');
    } catch (error) {
      logger.debug(
        `Error occurred while processing CFPAuthMethod ${method}:`,
        error
      );
    }
  };

  window.addEventListener('message', handleCfpAuthRequest, false);

  return () => {
    window.removeEventListener('message', handleCfpAuthRequest, false);
  };
};
